import React, { ChangeEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import { toast } from '../../../../../ui/toast';
import { TextArea } from '../../../../../ui/input/text_area/text_area';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { ToggleInput } from '../../../../../ui/input/toggle_input/toggle_input';
import {
  createAssessmentSettings,
  getAssessmentSettings,
  updateAssessmentSettings,
} from '../../../../../utils/api_service/assessment_api';
import { Permission } from '../../../../../utils/helpers/permissions';
import PermissionGate from '../../../../../utils/hooks/PermissionGate';
import usePermissions from '../../../../../utils/hooks/usePermissions';
import {
  AssessmentSettings,
  AssessmentType,
} from '../../../../../utils/types/assessment_types';

interface ISettingsProps {
  assessmentType: AssessmentType;
  assessmentId?: string;
  skipSpecificSettings?: boolean;
  setActiveTab: (value: ((prevState: string) => string) | string) => void;
}

interface SettingRowProps {
  label: string;
  description: string;
  children: React.ReactNode;
  isTextArea?: boolean;
}

const SettingRow: React.FC<SettingRowProps> = ({
  label,
  description,
  children,
  isTextArea,
}) => (
  <Stack
    direction={isTextArea ? 'column' : { xs: 'column', sm: 'row' }}
    justifyContent={isTextArea ? 'flex-start' : 'space-between'}
    alignItems={isTextArea ? 'stretch' : { xs: 'flex-start', sm: 'center' }}
    spacing={isTextArea ? 1.5 : 2}
    sx={{
      py: 1.5,
      px: 1,
      borderBottom: '1px solid',
      borderColor: 'divider',
      transition: 'background-color 150ms ease',
      '&:hover': { bgcolor: 'action.hover' },
    }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>
        {description}
      </Typography>
    </Box>
    <Box sx={{ width: isTextArea ? '100%' : 'auto', flexShrink: 0 }}>
      {children}
    </Box>
  </Stack>
);

export const Settings: React.FC<ISettingsProps> = ({
  assessmentType,
  assessmentId,
  skipSpecificSettings,
  setActiveTab,
}) => {
  const { id } = useParams<{ id: string }>();
  const { hasPermissions } = usePermissions();
  const [settingsExist, setSettingsExist] = useState<boolean>(false);
  const [settings, setSettings] = useState<AssessmentSettings>({
    assessmentId: assessmentId ?? id,
    showScore: false,
    customMessage: '',
    shuffleQuestions: false,
    enableCopy: false,
    attemptCount: '1',
    blurVideo: false,
    unsuccessfulEmail: false,
  });

  useEffect(() => {
    getAssessmentSettings(assessmentId ?? id).then(
      (response: AssessmentSettings) => {
        if (response.assessmentId) {
          setSettingsExist(true);
          setSettings({
            ...response,
            attemptCount: response.attemptCount.toString(),
          });
        }
      },
    );
  }, []);

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSettings({
      ...settings,
      [event.target.name]: event.target.value,
    });
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = settingsExist
        ? await updateAssessmentSettings(assessmentId ?? id, settings)
        : await createAssessmentSettings(settings);

      if (response.ok) {
        toast.success(
          `Assessment Settings ${
            settingsExist ? 'Updated' : 'Created'
          } Successfully!`,
          {
            position: 'bottom-center',
          },
        );
      }
    } catch (error) {
      toast.error('Failed to save settings. Please try again.', {
        position: 'bottom-center',
      });
    } finally {
      setIsLoading(false);
      setActiveTab('invitationTemplate');
    }
  };

  const canEdit = hasPermissions([Permission.ASSESSMENTS_EDIT]);

  const toggleSetting = (key: keyof AssessmentSettings) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const mainSettings = [
    {
      key: 'showScore' as keyof AssessmentSettings,
      label: 'Score Display',
      description:
        'Allow candidates to view their total score immediately after finishing the assessment.',
      show: !skipSpecificSettings,
    },
    {
      key: 'shuffleQuestions' as keyof AssessmentSettings,
      label: 'Shuffle Questions',
      description:
        'Randomize the order of questions for each candidate to prevent collaboration and cheating.',
      show: true,
    },
    {
      key: 'enableCopy' as keyof AssessmentSettings,
      label: 'Copy & Paste Protection',
      description:
        'Restrict clipboard operations (copy, paste, cut) within the assessment environment.',
      show: true,
    },
    {
      key: 'blurVideo' as keyof AssessmentSettings,
      label: 'Video Privacy',
      description:
        'Enable background blur or face blurring to protect candidate anonymity during the session.',
      show: true,
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', px: 2.5, py: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          General Configuration
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {mainSettings
          .filter((s) => s.show)
          .map((setting) => (
            <SettingRow
              key={setting.key}
              label={setting.label}
              description={setting.description}
            >
              <ToggleInput
                toggled={!!settings[setting.key]}
                handleToggle={() => toggleSetting(setting.key)}
                name={setting.key}
                informationText={`Enable ${setting.label.toLowerCase()}`}
                disabled={!canEdit}
              />
            </SettingRow>
          ))}

        {!skipSpecificSettings && (
          <SettingRow
            label="Attempt Limit"
            description="Specify the maximum number of times a candidate can attempt this assessment."
          >
            <Box sx={{ width: { xs: '100%', sm: 140 } }}>
              <TextInput
                name="attemptCount"
                value={settings.attemptCount}
                type="number"
                onChange={handleInputChange}
                min="1"
                max="10"
                disabled={!canEdit}
              />
            </Box>
          </SettingRow>
        )}

        {assessmentType === 'candidate' && !skipSpecificSettings && (
          <>
            <SettingRow
              label="Rejection Automation"
              description="Automatically send a standardized rejection email to unsuccessful candidates."
            >
              <ToggleInput
                toggled={settings.unsuccessfulEmail ?? false}
                handleToggle={() => toggleSetting('unsuccessfulEmail')}
                name="unsuccessfulEmail"
                informationText="Auto-notify"
                disabled={!canEdit}
              />
            </SettingRow>

            <SettingRow
              label="Success Threshold"
              description="The minimum percentage score required for a candidate to be considered successful."
            >
              <Box sx={{ width: { xs: '100%', sm: 140 } }}>
                <TextInput
                  name="thresholdPercent"
                  value={settings.thresholdPercent ?? '0'}
                  type="number"
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  disabled={!canEdit}
                />
              </Box>
            </SettingRow>
          </>
        )}

        {!skipSpecificSettings && (
          <SettingRow
            label="Custom Completion Message"
            description="This message will be displayed to candidates once they submit their assessment."
            isTextArea
          >
            <TextArea
              name="customMessage"
              value={settings.customMessage}
              onChange={handleInputChange}
              disabled={!canEdit}
            />
          </SettingRow>
        )}
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
          p: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontStyle: 'italic', display: { xs: 'none', sm: 'block' } }}
        >
          * Ensure all changes are verified before applying them to the live assessment.
        </Typography>
        <PermissionGate requiredPermissions={[Permission.ASSESSMENTS_EDIT]}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              fontSize: 12,
            }}
          >
            {isLoading ? 'Updating...' : 'Apply Changes'}
          </Button>
        </PermissionGate>
      </Stack>
    </Paper>
  );
};
